import type { AppLoginTokenContainer } from "@glide/common-core/dist/js/integration-types";
import { asAppLoginTokenContainer } from "@glide/common-core";
import { getEmailKey, getLoginTokenKey } from "@glide/support";
import { setCookie, getCookie, deleteCookie } from "./cookie-utils";

// Keep consistent with the token expiration time
const DEFAULT_COOKIE_EXPIRATION_DAYS = 30;

export function getAppLoginTokenAndEmailFromCookies(appID: string): {
    loginToken: AppLoginTokenContainer | undefined;
    email: string | undefined;
} {
    return {
        loginToken: getAppLoginTokenFromCookie(appID),
        email: getCookie(getEmailKey(appID)),
    };
}

export function setAppLoginTokenAndEmailInCookies(
    appID: string,
    loginToken: AppLoginTokenContainer | undefined,
    email: string
): void {
    // FIXME? We are copying the behavior that we used to have when storing this information in local
    // storage. Why can we get an undefined token here? Probably just to satisfy the types when parsing
    // the token from the backend response. We don't expect it to be undefined actually. But shouldn't
    // we catch the undefined case before reaching this function?
    if (loginToken !== undefined) {
        setAppLoginTokenInCookie(appID, loginToken);
    }
    setCookie(getEmailKey(appID), email, DEFAULT_COOKIE_EXPIRATION_DAYS);
}

export function clearAppLoginTokenAndEmailCookies(appID: string): void {
    deleteCookie(getLoginTokenKey(appID));
    deleteCookie(getEmailKey(appID));
}

/**
 * If the cookie is corrupted or invalid JSON, it will be automatically deleted
 * and undefined will be returned.
 */
function getAppLoginTokenFromCookie(appID: string): AppLoginTokenContainer | undefined {
    const cookieKey = getLoginTokenKey(appID);
    const tokenValue = getCookie(cookieKey);

    if (tokenValue === undefined) {
        return undefined;
    }

    try {
        return asAppLoginTokenContainer(JSON.parse(tokenValue));
    } catch {
        deleteCookie(cookieKey);
        return undefined;
    }
}

function setAppLoginTokenInCookie(appID: string, loginToken: AppLoginTokenContainer): void {
    const cookieKey = getLoginTokenKey(appID);
    const tokenValue = JSON.stringify(loginToken);
    setCookie(cookieKey, tokenValue, DEFAULT_COOKIE_EXPIRATION_DAYS);
}
